/**
 * THE GUEST-ONLY SURFACES AND THE ONE SIGNED-IN LANDING (th-49c64;
 * passwordless-sign-in D1, D12; th-1tcxp).
 *
 * A signed-in member has nothing to do on the sign-in door, so none is ever
 * left resting on it. Two addresses reach the door:
 *
 *   - a ROUTE whose record declares `meta: { guestOnly: true }`: `/sign-in`
 *     and `/register`;
 *   - the workspace's Authenticate PANEL, addressed by the flat
 *     `?horizontalTab=Authenticate&verticalTab=<form>` params that older
 *     invitation emails carry.
 *
 * Two moments put a signed-in member on one, and each has one owner, which
 * reads THIS module:
 *
 *   - NAVIGATING onto one (a bookmark, Back, a second tab, the post-sign-in
 *     re-boot, a retired password link, a view's own layout-crossing push):
 *     `router/index.js`'s global guard;
 *   - SIGNING IN: `stores/auth.js#signIn`, handed the start context the sign-in
 *     door captured when it opened (`stores/signInDoor.js`).
 *
 * Both send the member to `signedInLanding`, the ONE owner of where a signed-in
 * member lands. It lives here, apart from the router index, because the auth
 * store cannot import the index (the index imports the store) and the two must
 * never disagree. Its one import is the pure We address builder
 * (`utils/messageLinkState.js`, a leaf); it reads no store.
 *
 * ── IT RESOLVES FROM THE START CONTEXT, NEVER THE ROUTE AFTER SIGN-IN ──────
 * By the time a sign-in completes, a panel write may have rewritten `/sign-in`
 * to the workspace and dropped its query (th-1tcxp). So the door captures the
 * route it opened on, a same-origin `?redirect=` and the door's tokens (the
 * three invitations and the admission link, `startContextOf`), and the
 * landing resolves from that capture plus the sign-in answer's invitation
 * outcomes. The guard, which has no sign-in,
 * resolves from a context captured off the route it is entering.
 *
 * ── INVITATION LINKS HAVE ONE ACCEPTOR EACH ────────────────────────────────
 * A guest-only address can carry an invitation token. The door posts it with
 * the proof, and the server accepts it at sign-in (`Invitations::Acceptance`).
 * So a landing AFTER a sign-in carries no token at all: each one is consumed,
 * and a page that read it again would accept it a second time (D1). An
 * accepted hub invitation lands on that hub on We; `JoinDiscussionHub` would
 * redeem it again.
 *
 * A member who is ALREADY signed in consumed nothing, so the guard forwards
 * the token to the one surface that accepts that kind for a signed-in member:
 *
 *   - `hubInvitationToken` goes to `JoinDiscussionHub` (`/hubs/:token`), the v2
 *     join. Hub share links already point there, and its accept is idempotent:
 *     a member who already belongs gets `already_member`, and the join still
 *     lands on the hub;
 *   - team and user invitations have no signed-in acceptance here: sign-in
 *     and registration consume them. The member lands on the workspace. A
 *     share to an address is the one user invitation a signed-in member
 *     accepts, and its email links to its own page, `AcceptShare`
 *     (`/shared/:token`), which asks before it converts; it never routes
 *     through this guard.
 *
 * ── `?redirect=` IS HONORED ONLY FOR A SAME-ORIGIN PATH ────────────────────
 * The `requiresAuth` guard writes it (`to.fullPath`), so a member whose session
 * expired on a deep page returns there. Anything that does not resolve to this
 * origin (`https://evil.example/`, `//evil.example`) is ignored. A redirect
 * that points back at a guest surface is the guard's again, one hop shorter,
 * so it cannot loop.
 */
import { messageLinkState } from "@/utils/messageLinkState";

/** The flat `horizontalTab` value that opens the workspace's Authenticate panel. */
export const AUTHENTICATE_PANEL_TAB = "Authenticate";

/**
 * The three invitation tokens a sign-in URL can carry. The door posts the ones
 * it captured with every proof, and the server accepts them through its one
 * acceptor (D1). No landing after a sign-in carries any of them.
 */
export const INVITATION_TOKEN_PARAMS = Object.freeze([
  "hubInvitationToken",
  "teamInvitationToken",
  "userInvitationToken",
]);

/**
 * The signed admission link's token (waitlist-and-share-door W5). The
 * admission email and the recruit links name it `admission_token`. It is no
 * invitation, so it has no preview, but it rides every proof beside them and
 * the admission step's decision reads it.
 */
export const ADMISSION_TOKEN_PARAM = "admission_token";

/** Every token a door URL can carry: the three invitations and the admission link. */
export const DOOR_TOKEN_PARAMS = Object.freeze([...INVITATION_TOKEN_PARAMS, ADMISSION_TOKEN_PARAM]);

/**
 * The query params that a guest-only ROUTE hands on to the workspace for a
 * member who was already signed in: its position (`state`, the unified
 * router's param). Every other param on a guest-only route is guest-form
 * input (the invitation tokens, the sign-in `redirect`) and has no reader on
 * the workspace.
 */
const WORKSPACE_CARRIED_PARAMS = Object.freeze(["state"]);

/** A query value as one string (vue-router hands a repeated param back as an array). */
function firstValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

/**
 * `value` as a path on this origin, or null. Resolved through `URL`, as the
 * browser would resolve it, so `//evil.example`, `/\evil.example` and a path
 * hiding a tab or newline cannot pass for a local one.
 *
 * @param {string|string[]|null|undefined} value
 * @returns {string|null}
 */
export function sameOriginPath(value) {
  const candidate = firstValue(value);
  if (typeof candidate !== "string" || !candidate.startsWith("/")) return null;
  try {
    const origin = window.location.origin;
    const url = new URL(candidate, origin);
    return url.origin === origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
}

/**
 * The START CONTEXT of a sign-in: a plain snapshot of `route`, so it holds no
 * router internals and nothing later navigation can change, plus its vetted
 * `?redirect=` and its door tokens (`DOOR_TOKEN_PARAMS`), kept under
 * `invitationTokens`.
 *
 * @param {import("vue-router").RouteLocationNormalized|null} route
 * @returns {{ route: { name, path, fullPath, params, query, meta }|null,
 *             redirect: string|null, invitationTokens: Object<string, string> }}
 */
export function startContextOf(route) {
  const query = { ...(route?.query ?? {}) };
  const invitationTokens = {};
  for (const param of DOOR_TOKEN_PARAMS) {
    const token = firstValue(query[param]);
    if (token) invitationTokens[param] = String(token);
  }
  return {
    route: route
      ? {
          name: route.name ?? null,
          path: route.path ?? null,
          fullPath: route.fullPath ?? null,
          params: { ...(route.params ?? {}) },
          query,
          meta: { ...(route.meta ?? {}) },
        }
      : null,
    redirect: sameOriginPath(query.redirect),
    invitationTokens,
  };
}

/**
 * Does this route address a surface that only a guest can use?
 *
 * @param {{ meta?: object, query?: object }} route — a route location.
 * @returns {boolean}
 */
export function addressesGuestSurface(route) {
  return (
    Boolean(route?.meta?.guestOnly) ||
    firstValue(route?.query?.horizontalTab) === AUTHENTICATE_PANEL_TAB
  );
}

// The hub an accepted hub invitation joined, or null.
function acceptedHubId(invitations) {
  const hub = invitations.find(
    (outcome) => outcome?.kind === "hub" && outcome?.outcome === "accepted" && outcome?.hubId != null,
  );
  return hub ? hub.hubId : null;
}

// Whether a registration's sweep found shares waiting for the new member's
// address (`{ kind: "share", outcome: "accepted" }`, added by
// `Api::V2::RegistrationsController`, waitlist-and-share-door W8).
function sharesConverting(invitations) {
  return invitations.some((outcome) => outcome?.kind === "share" && outcome?.outcome === "accepted");
}

// The surface that lists what was shared with the member: the For You inbox,
// whose share rows arrive as the after-commit conversion grants them (each
// `share_grant_received` notice lifts the section and an open pane refetches).
// A fresh tree per landing, as `messageLinkState` builds the hub's.
function sharedWithYouState() {
  return {
    secondaryPanel: { horizontalTab: "Me", verticalTab: "ForYou" },
    meTab: { inboxSection: "for_you" },
  };
}

// A landing on the workspace that opens `workspaceState` there: a `?state=`
// section tree. The caller decides how it is applied (loaded with the page on
// a re-boot, merged in place otherwise), so the location itself carries none.
function onTheWorkspace(workspaceState) {
  return { location: { name: "MainMap", query: {} }, workspaceState };
}

/**
 * WHERE A SIGNED-IN MEMBER LANDS: the one resolution, for the guard and for
 * every sign-in.
 *
 * After a sign-in (`invitations` is the answer's outcome list), in order:
 *   1. an accepted hub invitation: that hub on We;
 *   2. a registration whose sweep found shares waiting for the address: the
 *      For You inbox, which lists what was shared with them. The conversion
 *      runs after the answer, so the landing trusts the sweep's outcome, never
 *      the grants, and the section fills as the grants arrive;
 *   3. a same-origin `?redirect=`: that path;
 *   4. the workspace, on Me ▸ For You (named, so a newcomer's welcome card is
 *      ready at the dock). Applied in place, that moves a door seated on the
 *      Authenticate panel off it, and a Me sub-tab still on the guest's Artemis.
 *
 * For a member who was already signed in (`invitations` is null: the guard's
 * arm), in order: an unconsumed hub invitation goes to `JoinDiscussionHub`; a
 * same-origin `?redirect=`; a guest-only route's workspace with its `state`;
 * the panel address's own page without the panel params, except that a panel
 * address at `/` (a legacy workspace address) lands on the workspace.
 *
 * The location never addresses a guest surface of its own making, so the
 * guard that redirects to it cannot loop.
 *
 * @param {ReturnType<typeof startContextOf>|null} startContext
 * @param {Array<{ kind: string, outcome: string, hubId?: number }>|null} [invitations]
 *   the sign-in answer's outcomes, or null when no sign-in consumed the tokens
 * @returns {{ location: import("vue-router").RouteLocationRaw, workspaceState: object|null }}
 *   `workspaceState` is a `?state=` section tree the landing opens on the
 *   workspace, or null when the location is the whole landing.
 */
export function signedInLanding(startContext, invitations = null) {
  const route = startContext?.route ?? {};
  const tokens = startContext?.invitationTokens ?? {};
  const signedInNow = Array.isArray(invitations);

  if (signedInNow) {
    const hubId = acceptedHubId(invitations);
    if (hubId != null) return onTheWorkspace(messageLinkState({ hubId }));
    if (sharesConverting(invitations)) return onTheWorkspace(sharedWithYouState());
  } else if (tokens.hubInvitationToken) {
    return {
      location: { name: "JoinDiscussionHub", params: { token: tokens.hubInvitationToken } },
      workspaceState: null,
    };
  }

  const redirect = sameOriginPath(startContext?.redirect);
  if (redirect) return { location: redirect, workspaceState: null };

  if (signedInNow) {
    return onTheWorkspace({ secondaryPanel: { horizontalTab: "Me", verticalTab: "ForYou" } });
  }

  const query = route.query ?? {};
  if (route.meta?.guestOnly) {
    const carried = {};
    for (const key of WORKSPACE_CARRIED_PARAMS) {
      if (query[key] != null) carried[key] = query[key];
    }
    return { location: { name: "MainMap", query: carried }, workspaceState: null };
  }

  // The panel address: the same page, minus the two params that open the panel.
  // At `/` it is a legacy workspace address (older invitation emails), so it
  // lands on the workspace itself, never leaning on the root fate's entry-only
  // forward (launch-marketing-pages M1).
  // eslint-disable-next-line no-unused-vars
  const { horizontalTab, verticalTab, ...rest } = query;
  if (route.path === "/") return { location: { name: "MainMap", query: rest }, workspaceState: null }; // lint:legacy-root-link
  return { location: { path: route.path, query: rest }, workspaceState: null };
}
